import { CertificateDto } from '@app/data/dtos/personal-data/certificate.dto';
import { CountryDto } from '@app/data/dtos/personal-data/country.dto';
import { CreateCertificateEmployeeDto } from '@app/data/dtos/personal-data/create-certificate-employee.dto';
import { CreateQualificationDto } from '@app/data/dtos/personal-data/create-qualification.dto';
import { EmployeeLanguagesDto } from '@app/data/dtos/personal-data/employee-languages.dto';
import { KnowledgeDto } from '@app/data/dtos/personal-data/knowledge.dto';
import { LanguageItemDto } from '@app/data/dtos/personal-data/language-item.dto';
import { LanguageLevelsDto } from '@app/data/dtos/personal-data/language-levels.dto';
import { LanguageMastersDto } from '@app/data/dtos/personal-data/language-masters.dto';
import { LanguageDto } from '@app/data/dtos/personal-data/language.dto';
import { MaritalStatusDto } from '@app/data/dtos/personal-data/marital-status.dto';
import { PaginationQualificationDto } from '@app/data/dtos/personal-data/pagination-qualification.dto';
import { HierarchyDto } from '@app/data/dtos/personal-data/personal-data/hierarchy.dto';
import { PersonalDataAddressDto } from '@app/data/dtos/personal-data/personal-data/personal-data-address.dto';
import { PersonalDataPhonesDto } from '@app/data/dtos/personal-data/personal-data/personal-data-phones.dto';
import { PersonalDataProfileDto } from '@app/data/dtos/personal-data/personal-data/personal-data-profile.dto';
import { PersonalDataDto } from '@app/data/dtos/personal-data/personal-data/personal-data.dto';
import { UpdateEmergencyContactDto } from '@app/data/dtos/personal-data/personal-data/update-emergency-contact.dto';
import { UpdatePhoneDto } from '@app/data/dtos/personal-data/personal-data/update-phone.dto';
import { PlatesDto } from '@app/data/dtos/personal-data/plates.dto';
import { QualificationEducationalLevelsDto } from '@app/data/dtos/personal-data/qualification-educational-levels.dto';
import { QualificationStatusDto } from '@app/data/dtos/personal-data/qualification-status.dto';
import { QualificationDto } from '@app/data/dtos/personal-data/qualification.dto';
import { QualificationStatusesDto } from '@app/data/dtos/personal-data/qualifications-statuses.dto';
import { StatusesDto } from '@app/data/dtos/personal-data/statuses.dto';
import { StudiesDto } from '@app/data/dtos/personal-data/studies.dto';
import { StudyDto } from '@app/data/dtos/personal-data/study.dto';
import { TestsDto } from '@app/data/dtos/personal-data/tests.dto';
import {
  Certificate,
  Knowledge,
  Language,
  LanguageItem,
  LanguageLevels,
  Statuses,
  Tests
} from '@app/entities/interfaces/personal-data/employee-languages.interface';
import { ModifyStudy, QualificationStatus } from '@app/entities/interfaces/personal-data/studies.interface';
import { Country } from '@app/entities/models/geography/country.model';
import { PersonalDataAddress } from '@app/entities/models/personal-data/address.model';
import {
  CertificateEmployeeModel,
  EmployeeLanguages,
  LanguageMastersModel
} from '@app/entities/models/personal-data/employee-languages.model';
import { HierarchyItem } from '@app/entities/models/personal-data/hierarchyItem.model';
import { MaritalStatus } from '@app/entities/models/personal-data/marital-status.model';
import { PersonalDataPhones } from '@app/entities/models/personal-data/personal-data-phones';
import { PersonalDataProfile } from '@app/entities/models/personal-data/personal-data-profile.model';
import { PersonalData } from '@app/entities/models/personal-data/personal-data.model';
import { Plate } from '@app/entities/models/personal-data/plates.model';
import {
  PaginationQualification,
  Qualification,
  QualificationEducationalLevels,
  QualificationStatuses,
  Studies,
  Study
} from '@app/entities/models/personal-data/studies.model';
import { UpdateEmergencyContact } from '@app/entities/models/personal-data/update-emergency-contact.model';
import { UpdatePhone } from '@app/entities/models/personal-data/update-phone.model';

import { MapFromFn } from '@mercadona/common/public';
import { Builder } from '@mercadona/common/utils';

import { mapIdDescripcion, mapIdName } from '../shared/shared.mappers';

export const mapPersonalInfo: MapFromFn<PersonalDataDto, PersonalData> = (value: PersonalDataDto): PersonalData =>
  Builder.forModel(PersonalData)
    .profile(mapProfile(value.profile))
    .phones(
      value.phones
        .map((phone: PersonalDataPhonesDto): PersonalDataPhones | null => mapPhones(phone))
        .filter((phone: PersonalDataPhones | null): phone is PersonalDataPhones => phone !== null)
    )
    .address(mapAddress(value.address))
    .fullName(`${value.profile.name}  ${value.profile.first_surname} ${value.profile.second_surname}`)
    .emergencyContact(mapPhones(value.emergency_phone) ?? undefined)
    .email(value.email)
    .build();

export const mapProfile: MapFromFn<PersonalDataProfileDto, PersonalDataProfile> = (
  value: PersonalDataProfileDto
): PersonalDataProfile =>
  Builder.forModel(PersonalDataProfile)
    .name(value.name)
    .firstSurname(value.first_surname)
    .secondSurname(value.second_surname)
    .identificationDocument(value.identification_document)
    .civilStatusId(value.civil_status_id)
    .civilStatusName(value.civil_status_name)
    .fiscalAlert(value.fiscal_alert)
    .build();

export const mapMaritalStatus: MapFromFn<MaritalStatusDto, MaritalStatus> = (value: MaritalStatusDto): MaritalStatus =>
  Builder.forModel(MaritalStatus).civilStatusId(value.civil_status_id).name(value.name).build();

export const mapDataMaritalStatus: MapFromFn<MaritalStatusDto[], MaritalStatus[]> = (value) =>
  value.map(mapMaritalStatus);

export const mapAddress: MapFromFn<PersonalDataAddressDto, PersonalDataAddress> = (
  value: PersonalDataAddressDto
): PersonalDataAddress => {
  const province: HierarchyDto | undefined = value.hierarchy.find((place: HierarchyDto): boolean => place.order === 1);
  const city: HierarchyDto | undefined = value.hierarchy.find((place: HierarchyDto): boolean => place.order === 2);

  return Builder.forModel(PersonalDataAddress)
    .id(value.address_id)
    .principal(value.is_principal)
    .streetName(value.street_name)
    .streetNumber(value.street_number)
    .floorNumber(value.floor_number)
    .stairway(value.stairway)
    .doorNumber(value.door_number)
    .streetTypeId(value.street_type_id)
    .streetTypeName(value.street_type_name)
    .countryIsoNumericCode(value.country_iso_numeric_code)
    .postalCode(value.postal_code)
    .hierarchyCode(value.hierarchy_code)
    .hierarchy(
      value.hierarchy
        .map(mapHierarchy)
        .filter((item: HierarchyItem | undefined): item is HierarchyItem => item !== undefined)
    )
    .province(province ? mapHierarchy(province) : undefined)
    .city(city ? mapHierarchy(city) : undefined)
    .build();
};

export const mapPhones: MapFromFn<PersonalDataPhonesDto | null, PersonalDataPhones | null> = (
  value: PersonalDataPhonesDto | null
): PersonalDataPhones | null => {
  if (!value) {
    return null;
  }

  return Builder.forModel(PersonalDataPhones)
    .id(value.id)
    .countryPrefixCode(value.country_prefix_code)
    .numberPhone(value.number)
    .countryIsoNumericCode(value.country_iso_numeric_code)
    .type(value.type)
    .isPrincipal(value.is_principal)
    .typeName(value.type_name)
    .emergencyPersonName(value.emergency_person_name)
    .emergencyPersonKinship(value.emergency_person_kinship)
    .isEmergency(value.is_emergency)
    .build();
};

export const mapUpdatePhones: MapFromFn<UpdatePhone, UpdatePhoneDto> = (value: UpdatePhone): UpdatePhoneDto =>
  Builder.forDto<UpdatePhoneDto>()
    .number(value.numberPhone)
    .type(value.type)
    .country_prefix_code(value.countryPrefixCode)
    .country_iso_numeric_code(value.countryIsoNumericCode)
    .build();

export const mapUpdateEmergencyContact: MapFromFn<UpdateEmergencyContact, UpdateEmergencyContactDto> = (
  value: UpdateEmergencyContact
): UpdateEmergencyContactDto =>
  Builder.forDto<UpdateEmergencyContactDto>()
    .number(value.numberPhone)
    .country_prefix_code(value.countryPrefixCode)
    .country_iso_numeric_code(value.countryIsoNumericCode)
    .emergency_person_name(value.emergencyPersonName)
    .emergency_person_kinship(value.emergencyPersonKinship)
    .build();

const mapHierarchyItemToDto: MapFromFn<HierarchyItem, HierarchyDto> = (item: HierarchyItem) => ({
  id: item.id,
  alternative_code: item.alternativeCode,
  name: item.name,
  order: item.order
});

export const mapUpdateAddress: MapFromFn<PersonalDataAddress, PersonalDataAddressDto> = (
  value: PersonalDataAddress
): PersonalDataAddressDto =>
  Builder.forDto<PersonalDataAddressDto>()
    .address_id(value.id)
    .is_principal(value.principal)
    .street_name(value.streetName)
    .street_number(value.streetNumber)
    .floor_number(value.floorNumber)
    .stairway(value.stairway)
    .door_number(value.doorNumber)
    .street_type_id(value.streetTypeId)
    .street_type_name(value.streetTypeName)
    .country_iso_numeric_code(value.countryIsoNumericCode)
    .postal_code(value.postalCode)
    .hierarchy(value.hierarchy.map(mapHierarchyItemToDto))
    .build();

export const mapKnowledge: MapFromFn<KnowledgeDto, Knowledge> = (value) =>
  Builder.forModel(Knowledge)
    .language(mapLanguage(value.language))
    .languageLevel(mapLanguageLevels(value.language_level))
    .certificate(value.certificate)
    .build();

export const mapLanguage: MapFromFn<LanguageDto, Language> = (value) =>
  Builder.forModel(Language).id(value.id).description(value.description).build();

export const mapLanguageItem: MapFromFn<LanguageItemDto, LanguageItem> = (value) =>
  Builder.forModel(LanguageItem).id(value.id).code(value.code).description(value.description).build();

export const mapTests: MapFromFn<TestsDto, Tests> = (value) =>
  Builder.forModel(Tests)
    .language(mapLanguageItem(value.language))
    .levelTest(mapIdDescripcion(value.level_test))
    .testDate(value.test_date)
    .testReason(value.test_reason)
    .build();

export const mapEmployeeLanguages: MapFromFn<EmployeeLanguagesDto, EmployeeLanguages> = (value) =>
  Builder.forModel(EmployeeLanguages)
    .knowledge(value.knowledge.map(mapKnowledge))
    .tests(value.tests.map(mapTests))
    .build();

export const mapLanguageMasters: MapFromFn<LanguageMastersDto, LanguageMastersModel> = (value) =>
  Builder.forModel(LanguageMastersModel)
    .languages(value.languages.map(mapLanguageItem))
    .tests(value.tests.map(mapIdDescripcion))
    .levels(value.levels?.map(mapLanguageLevels))
    .statuses(value.statuses.map(mapStatuses))
    .build();

export const mapCertificate: MapFromFn<CertificateDto, Certificate> = (value) =>
  Builder.forModel(Certificate).date(value.date).status(mapStatuses(value.status)).build();

export const mapStatuses: MapFromFn<StatusesDto, Statuses> = (value) =>
  Builder.forModel(Statuses).id(value.id).description(value.description).build();

export const mapLanguageLevels: MapFromFn<LanguageLevelsDto, LanguageLevels> = (value) =>
  Builder.forModel(LanguageLevels)
    .id(value.id)
    .shortDescription(value.short_description)
    .longDescription(value.long_description)
    .build();

export const mapStudy: MapFromFn<StudyDto, Study> = (value) =>
  Builder.forModel(Study)
    .startYear(value.start_year)
    .endYear(value.end_year)
    .academy(value.academy)
    .country(mapCountry(value.country))
    .qualification(value.qualification)
    .educationalLevel(value.educational_level)
    .qualificationStatus(mapQualificationStatus(value.qualification_status))
    .build();

export const mapStudies: MapFromFn<StudiesDto, Studies> = (value) =>
  Builder.forModel(Studies).qualifications(value.qualifications.map(mapStudy)).build();

export const mapCreateQualificationDto: MapFromFn<ModifyStudy, CreateQualificationDto> = (value) =>
  Builder.forDto<CreateQualificationDto>()
    .country_iso_numeric_code(value.country)
    .qualification_id(value.qualification)
    .start_year(value.startYear)
    .end_year(value.endYear || undefined)
    .qualification_status_id(value.status.id)
    .academy(value.academy || '')
    .build();

export const mapCountry: MapFromFn<CountryDto, Country> = (value) =>
  Builder.forModel(Country)
    .hierarchyCode(value.hierarchy_code)
    .name(value.name)
    .countryIsoNumericCode(value.country_iso_numeric_code)
    .build();

export const mapDataCountry: MapFromFn<CountryDto[], Country[]> = (value) => value.map(mapCountry);

export const mapQualification: MapFromFn<QualificationDto, Qualification> = (value) =>
  Builder.forModel(Qualification)
    .data(value.data.map(mapIdName))
    .pagination(mapPaginationQualification(value.pagination))
    .build();

export const mapPaginationQualification: MapFromFn<PaginationQualificationDto, PaginationQualification> = (value) =>
  Builder.forModel(PaginationQualification)
    .requestedPage(value.requested_page)
    .requestedSize(value.requested_size)
    .retrievedResults(value.retrieved_results)
    .totalResults(value.total_results)
    .build();

export const mapHierarchy: MapFromFn<HierarchyDto | undefined, HierarchyItem | undefined> = (value) =>
  value
    ? Builder.forModel(HierarchyItem)
        .id(value.id)
        .alternativeCode(value.alternative_code)
        .name(value.name)
        .order(value.order)
        .build()
    : undefined;

export const mapQualificationStatus: MapFromFn<QualificationStatusDto, QualificationStatus> = (value) =>
  Builder.forModel(QualificationStatus)
    .id(value.id)
    .name(value.name)
    .endDate(value.end_date)
    .endDateText(value.end_date_text)
    .build();

export const mapQualificationStatuses: MapFromFn<QualificationStatusesDto, QualificationStatuses> = (value) =>
  Builder.forModel(QualificationStatuses)
    .qualificationStatuses(value.qualification_statuses.map(mapQualificationStatus))
    .build();

export const mapQualificationEducationalLevels: MapFromFn<
  QualificationEducationalLevelsDto,
  QualificationEducationalLevels
> = (value) =>
  Builder.forModel(QualificationEducationalLevels).educationalLevels(value.educational_levels.map(mapIdName)).build();

export const mapCertificateEmployee: MapFromFn<CreateCertificateEmployeeDto, CertificateEmployeeModel> = (value) =>
  Builder.forModel(CertificateEmployeeModel)
    .language(value.language)
    .languageLevel(value.language_level)
    .certificate(value.certificate)
    .build();

export const mapCertificateEmployeeDto: MapFromFn<CertificateEmployeeModel, CreateCertificateEmployeeDto> = (value) =>
  Builder.forDto<CreateCertificateEmployeeDto>()
    .language(value.language)
    .language_level(value.languageLevel)
    .certificate(value.certificate)
    .build();

export const mapLicensePlates: MapFromFn<PlatesDto, Plate[]> = (dto) =>
  dto.license_plates.map((lp) => Builder.forModel(Plate).licensePlate(lp).build());

export const mapPlatesToDto: MapFromFn<Plate[], PlatesDto> = (plates) => ({
  license_plates: plates.map((p) => p.licensePlate)
});
